/* eslint-disable */

import { getOrderType, getUnitConvertedUnit } from '@/Helpers';
import moment from 'moment';

// Create word map from excel product names
export const createExcelProductMap = (clipboardRows, itemNameColumn) => {
	//console.log('=== createExcelProductMap ===');
	//console.log('clipboardRows:', clipboardRows);
	//console.log('itemNameColumn:', itemNameColumn);

	const excelProductMap = {};

	clipboardRows.forEach((row, index) => {
		const originalName = row[itemNameColumn];
		//console.log(`Row ${index}: originalName = "${originalName}"`);

		if (!originalName) {
			//console.log(`Row ${index}: Skipping - no originalName`);
			return;
		}

		// Step 1: Replace all special characters with spaces and remove trailing numbers
		const processedName = originalName
			.replace(/\s+\d+\s*$/, '') // Remove trailing numbers like " 2508"
			.replace(/[^\w\s]/g, ' ') // Replace special chars with space
			.toLowerCase()
			.replace(/\s+/g, ' ') // Normalize multiple spaces to single space
			.trim();

		//console.log(`Row ${index}: processedName = "${processedName}"`);

		// Step 2: Split into words array and filter meaningful words
		const splitArray = processedName
			.split(' ')
			.filter((word) => word.length > 0); // Keep all non-empty words

		//console.log(`Row ${index}: splitArray =`, splitArray);

		// Store in map: original name -> split array
		excelProductMap[originalName] = splitArray;
	});

	//console.log('Final excelProductMap:', excelProductMap);
	return excelProductMap;
};

// Find best matching excel product for a modal item
export const findBestExcelMatch = (modalItemName, excelProductMap) => {
	//console.log('\n=== findBestExcelMatch ===');
	//console.log('modalItemName:', modalItemName);
	//console.log('excelProductMap:', excelProductMap);

	if (!modalItemName) {
		//console.log('No modalItemName provided, returning null');
		return null;
	}

	// Process modal item name same way
	const modalProcessedName = modalItemName
		.replace(/\s+\d+\s*$/, '') // Remove trailing numbers
		.replace(/[^\w\s]/g, ' ') // Replace special chars with space
		.toLowerCase()
		.replace(/\s+/g, ' ') // Normalize spaces
		.trim();

	//console.log('modalProcessedName:', modalProcessedName);

	const modalWords = modalProcessedName
		.split(' ')
		.filter((word) => word.length > 0);

	//console.log('modalWords:', modalWords);

	let bestMatch = null;
	let maxMatches = 0;

	// Step 3: Compare with each excel product
	let bestMatchInfo = null;

	Object.entries(excelProductMap).forEach(([excelName, excelWords]) => {
		//console.log(`\nComparing with excel product: "${excelName}"`);
		//console.log('excelWords:', excelWords);

		// Count matching words
		const matchCount = modalWords.filter((modalWord) => {
			const wordMatches = excelWords.some((excelWord) =>
				modalWord === excelWord ||
				modalWord.includes(excelWord) ||
				excelWord.includes(modalWord)
			);
			//console.log(`  Modal word "${modalWord}" matches: ${wordMatches}`);
			return wordMatches;
		}).length;

		//console.log(`  Total matches: ${matchCount}`);

		// Calculate match confidence
		const totalWords = Math.max(modalWords.length, excelWords.length);
		const confidence = matchCount / totalWords;

		// Require at least 2 words AND at least 50% confidence
		if (matchCount >= 2 && confidence >= 0.5 && matchCount > maxMatches) {
			//console.log(`  New best match! Previous: ${maxMatches}, New: ${matchCount}`);
			maxMatches = matchCount;
			bestMatch = excelName;
			bestMatchInfo = {
				excelName,
				matchCount,
				totalWords,
				confidence,
				isLowConfidence: confidence < 0.7 && matchCount === 2 // Low confidence: only 2 matches with more total words
			};
		}
	});

	//console.log(`\nFINAL RESULT: Modal: "${modalItemName}" -> Excel: "${bestMatch}" (${maxMatches} matches)`);
	if (bestMatchInfo) {
		//console.log(`Confidence: ${(bestMatchInfo.confidence * 100).toFixed(1)}%, Low confidence: ${bestMatchInfo.isLowConfidence}`);
	}

	return bestMatchInfo;
};

// Parse TSV data into structured format
export const parseTSVData = (tsvText) => {
	//console.log('\n=== parseTSVData ===');
	//console.log('Input tsvText length:', tsvText ? tsvText.length : 0);
	//console.log('Input tsvText preview:', tsvText ? tsvText.substring(0, 200) + '...' : 'null');

	if (!tsvText || typeof tsvText !== 'string') {
		//console.log('Invalid input - not a string or empty');
		return null;
	}

	const lines = tsvText.trim().split('\n');
	//console.log('Number of lines:', lines.length);
	//console.log('First line (headers):', lines[0]);

	if (lines.length < 2) {
		//console.log('Not enough lines (need at least 2)');
		return null;
	}

	// Parse headers and remove leading empty column if present
	let headers = lines[0].split('\t').map(h => h.trim());
	//console.log('Raw headers:', headers);

	// Check if first header is empty (indicating leading tab)
	if (headers[0] === '') {
		//console.log('Detected leading tab - removing first empty column');
		headers = headers.slice(1); // Remove the first empty header
	}

	//console.log('Final headers:', headers);

	const rows = [];

	for (let i = 1; i < lines.length; i++) {
		//console.log(`Processing line ${i}:`, lines[i]);
		let cells = lines[i].split('\t');
		//console.log(`  Raw cells (${cells.length}):`, cells);

		// If line starts with tab, remove the first empty cell
		if (cells[0] === '') {
			//console.log('  Removing leading empty cell');
			cells = cells.slice(1);
		}

		//console.log(`  Final cells (${cells.length}):`, cells);

		if (cells.length > 1) { // Skip empty lines
			const row = {};
			headers.forEach((header, index) => {
				const cellValue = cells[index] ? cells[index].trim() : '';
				row[header] = cellValue;
				//console.log(`    ${header} = "${cellValue}"`);
			});
			rows.push(row);
			//console.log(`  Added row:`, row);
		} else {
			//console.log(`  Skipping line ${i} - not enough cells`);
		}
	}

	//console.log('Final parsed data:');
	//console.log('Headers:', headers);
	//console.log('Rows:', rows);

	return { headers, rows };
};

// Create store mapping from clipboard headers to table headers
export const createStoreMapping = (clipboardHeaders, tableHeaders, itemNameColumn, skuCodeColumn = null) => {
	// console.log('\n=== createStoreMapping ===');
	// console.log('clipboardHeaders:', clipboardHeaders);
	// console.log('tableHeaders:', tableHeaders);
	// console.log('itemNameColumn:', itemNameColumn);

	const storeMapping = {};

	clipboardHeaders.forEach(clipboardHeader => {
		//console.log(`\nProcessing clipboard header: "${clipboardHeader}"`);

		if (clipboardHeader === itemNameColumn || clipboardHeader === skuCodeColumn) {
			//console.log('  Skipping - this is the item name or sku code column');
			return;
		}

		// Find matching store in table headers
		const matchingTableHeader = tableHeaders.find(tableHeader => {
			//console.log(`  Checking against table header: ${tableHeader.name} (id: ${tableHeader.id})`);

			if (tableHeader.id === 'Item Name' || tableHeader.id === 'SkuCode' || tableHeader.id === 'Total') {
				//console.log('    Skipping - this is a fixed column');
				return false;
			}

			// Match by store shortName
			const nameMatch = tableHeader.name &&
				(tableHeader.storeName?.toLowerCase() === clipboardHeader.toLowerCase() || tableHeader.shortName?.toLowerCase() === clipboardHeader.toLowerCase())
			// tableHeader.name.toLowerCase().includes(clipboardHeader.toLowerCase()) ||
			// clipboardHeader.toLowerCase().includes(tableHeader.name.toLowerCase()));

			// console.log(`    Name match result: ${nameMatch}`);
			return nameMatch;
		});

		if (matchingTableHeader) {
			//console.log(`  MATCH FOUND: "${clipboardHeader}" -> "${matchingTableHeader.name}" (id: ${matchingTableHeader.id})`);
			storeMapping[clipboardHeader] = matchingTableHeader.id;
		} else {
			//console.log(`  NO MATCH FOUND for "${clipboardHeader}"`);
		}
	});
	// console.log('Final storeMapping:', storeMapping);
	return storeMapping;
};

// Find item name column in clipboard headers
export const findItemNameColumn = (clipboardHeaders) => {
	//console.log('\n=== findItemNameColumn ===');
	//console.log('clipboardHeaders:', clipboardHeaders);

	const itemNameColumn = clipboardHeaders.find(h => {
		const isMatch = (h.toLowerCase().includes('item') && h.toLowerCase().includes('name')) || (h.toLowerCase().includes(' list') || h.toLowerCase().includes('product') && h.toLowerCase().includes('name'));
		//console.log(`  Header "${h}": isMatch = ${isMatch}`);
		return isMatch;
	});

	//console.log('Found item name column:', itemNameColumn);
	return itemNameColumn;
};

// Find SKU code column in clipboard headers
export const findSkuCodeColumn = (clipboardHeaders) => {
	const skuKeywords = ['skucode', 'sku code', 'sku', 'client variant', 'clientvariantid', 'variant id', 'itemcode', 'item code', 'Item Code', 'Product Code', 'productCode', 'productcode'];
	const skuCodeColumn = clipboardHeaders.find(h => {
		const headerLower = h.toLowerCase().trim();
		return skuKeywords.some(keyword => headerLower === keyword || headerLower.includes(keyword));
	});
	return skuCodeColumn || null;
};

// ── Updated normalizeName that handles the most common hidden-char culprits ──
const normalizeName = (name = '') =>
	name
		.normalize('NFC')                                      // normalize unicode (é vs é)
		.replace(/\(-\).*/gi, '')                             // Strip (-) and everything after it (e.g. (-)WYNRM667)
		.replace(/\(\s*[- ]*\d+\s*(gms?|g|kgs?|mls?|ltr?s?|pcs?|pc|)\).*/gi, '')   // Strip (100 gm), (-100 gms), (50) and everything after
		.replace(/\s+[- ]*\d+\s*(gms?|g|kgs?|mls?|ltr?s?|pcs?|pc).*/gi, '')      // Strip standalone weight and everything after it
		.replace(/\s+[a-z]{0,5}\s*\d+\s*$/gi, '')             // Strip trailing SKU suffixes like ' wyn10' or ' Emp001'
		.replace(/[^\x20-\x7E]/g, '')                          // strip ALL non-ASCII chars (hidden unicode, NBSP, ZWS etc.)
		.replace(/[^\w\s]/g, '')                               // punctuation
		.toLowerCase()
		.replace(/\s+/g, ' ')                                  // collapse whitespace
		.trim();


// ── findProductsInDropdown ────────────────────────────────────────────────────
export const findProductsInDropdown = (excelProductMap, dropdownProducts, usedProducts = new Set()) => {
	const foundProducts = [];
	Object.entries(excelProductMap).forEach(([excelName, excelWords]) => {
		const excelNorm = normalizeName(excelWords.join(' '));

		let matched = null;

		for (const product of dropdownProducts) {
			if (usedProducts.has(product.value)) continue;

			const productNorm = normalizeName(product.label);
			if (productNorm === excelNorm) {
				matched = product;
				break;
			}
		}

		if (matched) {
			foundProducts.push({
				excelName,
				product: matched,
				matchType: 'perfect',
				identified: true,
			});
		} else {
			foundProducts.push({
				excelName,
				product: null,
				matchType: 'none',
				identified: false,
			});
		}
	});

	return foundProducts;
};
// Create new row from product data
export const createNewRowFromProduct = (product, stores, serialNo = '') => {
	return {
		'S.No': serialNo,
		'Item Name': product.viewOnlyLabel,
		'SkuCode': product.clientVariantId,
		'itemId': product.value,
		'Total': '',
		...stores?.reduce((acc, store) => {
			acc[`${store.id}`] = {
				qty: 0,
				unit: getUnitConvertedUnit(null, product.unit, true),
				storeId: store.id,
				itemId: product.value,
				productId: product.productId,
			};
			return acc;
		}, {})
	};
};

// Update table rows with matched excel data (clientVariantId priority matching)
export const updateTableRowsWithExcelData = (tableRows, clipboardRows, excelProductMap, storeMapping, itemNameColumn, dropdownProducts, stores, skuCodeColumn = null) => {
	// console.log('\n=== updateTableRowsWithExcelData (NEW VERSION - clientVariantId priority) ===');
	// console.log('clipboardRows:', clipboardRows);
	// console.log('excelProductMap:', excelProductMap);
	// console.log('storeMapping:', storeMapping);
	// console.log('stores:', stores);
	// console.log('itemNameColumn:', itemNameColumn);
	// console.log('dropdownProducts count:', dropdownProducts);

	// IGNORE EXISTING TABLE ROWS - START FROM SCRATCH
	//console.log('Ignoring existing table rows, starting fresh...');

	const finalRows = [];
	let matchedCount = 0;
	let updatedCount = 0;
	let addedCount = 0;
	let serialCounter = 1;

	// Track used products to prevent duplicates
	const usedProducts = new Set();
	//console.log('Tracking used products to prevent duplicates...');

	// Build a lookup map from SKU code column values to excel names if skuCodeColumn exists
	const skuToExcelName = {};
	if (skuCodeColumn) {
		clipboardRows.forEach(row => {
			const excelName = row[itemNameColumn];
			const skuValue = row[skuCodeColumn]?.toString()?.trim();
			if (excelName && skuValue) {
				skuToExcelName[excelName] = skuValue;
			}
		});
	}

	// Process each Excel item in order
	Object.entries(excelProductMap).forEach(([excelName, excelWords]) => {
		//console.log(`\n--- Processing Excel item: "${excelName}" ---`);
		let currentRow = null;
		let isNewRow = false;
		let matchType = 'none';

		// STEP 0: If SKU code column exists, try matching by SKU code column value first
		if (skuCodeColumn && skuToExcelName[excelName]) {
			const skuValue = skuToExcelName[excelName];
			const foundProduct = dropdownProducts.find(p =>
				p.clientVariantId?.toString() === skuValue ||
				p.clientVariantId === parseInt(skuValue, 10)
			);
			if (foundProduct && !usedProducts.has(foundProduct.value)) {
				currentRow = createNewRowFromProduct(foundProduct, stores, serialCounter++);
				currentRow._matchType = 'skuColumn';
				currentRow._clientVariantId = skuValue;
				matchType = 'skuColumn';
				isNewRow = true;
				addedCount++;
				matchedCount++;
				usedProducts.add(foundProduct.value);
			}
		}

		// STEP 1: TRY clientVariantId MATCHING FROM NAME (trailing number)
		if (!currentRow) {
			//console.log('Step 1: Trying clientVariantId matching...');
			const clientVariantResult = getClientVariantIdFromExcelName(excelName, dropdownProducts);
			if (clientVariantResult && Array.isArray(clientVariantResult)) {
				const [clientVariantId, foundProduct] = clientVariantResult;

				if (foundProduct && !usedProducts.has(foundProduct.value)) {
					//console.log(`✅ ClientVariantId match found: ${foundProduct.label} (ID: ${clientVariantId})`);
					currentRow = createNewRowFromProduct(foundProduct, stores, serialCounter++);
					currentRow._matchType = 'clientVariantId';
					currentRow._clientVariantId = clientVariantId;
					matchType = 'clientVariantId';
					isNewRow = true;
					addedCount++;
					matchedCount++;

					// Mark product as used to prevent duplicates
					usedProducts.add(foundProduct.value);
					//console.log(`  Marked product ${foundProduct.value} as used`);
				} else if (foundProduct && usedProducts.has(foundProduct.value)) {
					//console.log(`⚠️ ClientVariantId match found but product ${foundProduct.label} already used - skipping duplicate`);
				} else {
					//console.log(`⚠️ ClientVariantId ${clientVariantId} extracted but no product found in dropdown`);
				}
			} else {
				//console.log('❌ No clientVariantId extracted from Excel name');
			}
		}

		// STEP 2: FALLBACK TO NAME MATCHING (IMPROVED LOGIC)
		if (!currentRow) {
			//console.log('Step 2: Falling back to name matching...');

			// Use improved dropdown matching logic with duplicate prevention
			const dropdownMatches = findProductsInDropdown({ [excelName]: excelWords }, dropdownProducts, usedProducts);
			const dropdownMatch = dropdownMatches.find(dm => dm.excelName === excelName && dm.matchType === 'perfect');

			if (dropdownMatch) {
				//console.log(`✅ Name match found: ${dropdownMatch.product.label}`);
				currentRow = createNewRowFromProduct(dropdownMatch.product, stores, serialCounter++);
				currentRow._matchType = 'nameMatching';
				currentRow._isNewFromDropdown = true;
				matchType = 'nameMatching';
				isNewRow = true;
				addedCount++;
				matchedCount++;

				// Mark product as used to prevent duplicates
				usedProducts.add(dropdownMatch.product.value);
				//console.log(`  Marked product ${dropdownMatch.product.value} as used`);
			} else {
				//console.log('❌ No name match found');
			}
		}

		// STEP 3: CREATE UNIDENTIFIED PLACEHOLDER (UNCHANGED LOGIC)
		if (!currentRow) {
			//console.log('Step 3: Creating unidentified placeholder...');
			// Create placeholder row for non-found Excel item (KEEP SAME LOGIC)
			currentRow = {
				'S.No': serialCounter++,
				'Item Name': excelName,
				'itemId': null,
				'SkuCode': '',
				'Total': '',
				_isNotFound: true,
				_excelName: excelName,
				_matchType: 'unidentified',
				...stores?.reduce((acc, store) => {
					acc[`${store.id}`] = {
						qty: 0,
						unit: '',
						storeId: store.id,
						itemId: null,
						productId: null,
					};
					return acc;
				}, {})
			};
			matchType = 'unidentified';
		}

		if (currentRow) {
			// Find the excel row data for quantities
			const excelRowData = clipboardRows.find(row => row[itemNameColumn] === excelName);

			if (excelRowData) {
				// Update quantities for matched stores
				Object.entries(storeMapping).forEach(([clipboardHeader, storeId]) => {
					const quantity = excelRowData[clipboardHeader];
					//console.log(`  Store ${clipboardHeader} -> ${storeId}: quantity = "${quantity}"`);

					if (quantity && quantity !== '-' && !Number.isNaN(parseFloat(quantity)) && parseFloat(quantity) > 0) {
						//console.log(`    Valid quantity: ${parseFloat(quantity)}`);
						if (currentRow[storeId]) {
							//console.log(`    Updating store ${storeId} with quantity ${parseFloat(quantity)}`);
							currentRow[storeId].qty = parseFloat(quantity);
							updatedCount++;
							//console.log(`    updatedCount now: ${updatedCount}`);
						} else {
							//console.log(`    Store ${storeId} not found in row`);
						}
					} else {
						//console.log(`    Invalid or zero quantity: "${quantity}" - setting to 0`);
						// Set qty to 0 for empty or invalid Excel row cells
						if (currentRow[storeId]) {
							currentRow[storeId].qty = 0;
						}
					}
				});
			}

			finalRows.push(currentRow);
			//console.log(`✅ Added row: ${excelName} (match: ${matchType})`);
		}
	});

	//console.log('\n=== Final Results (PERFECT MATCH PRIORITY) ===');
	//console.log('- clientVariantId matches: prioritized first');
	//console.log('- Name matches: PERFECT > EXACT > PARTIAL (no more includes issues)'); 
	//console.log('- Perfect matches: processed names exactly equal (score: 1000)');
	//console.log('- Exact matches: all words match perfectly (score: 100+)');
	//console.log('- Partial matches: includes logic as fallback (score: 1-99)');
	//console.log('- Duplicate prevention: same product cannot appear twice');
	//console.log('- Unidentified products: preserved same logic');
	//console.log('matchedCount:', matchedCount);
	//console.log('updatedCount:', updatedCount);
	//console.log('addedCount:', addedCount);
	//console.log('finalRows count:', finalRows.length);

	return {
		updatedRows: finalRows,
		matchedCount,
		updatedCount,
		addedCount
	};
};

// Extract clientVariantId from Excel name (trailing number after removing special characters)
export const getClientVariantIdFromExcelName = (excelName, productList = [], customerId = null) => {
	//console.log('\n=== getClientVariantIdFromExcelName ===');
	//console.log('excelName:', excelName);
	//console.log('productList length:', productList.length);
	//console.log('customerId:', customerId);

	if (!excelName || typeof excelName !== 'string') {
		//console.log('Invalid excelName provided');
		return null;
	}

	// Step 1: Extract trailing number from Excel name
	// Look for numbers at the end, optionally preceded by spaces or special characters
	const trailingNumberMatch = excelName.match(/[^\w]*(\d+)\s*$/);
	if (!trailingNumberMatch) {
		//console.log('No trailing number found in Excel name');
		return null;
	}

	const extractedNumber = trailingNumberMatch[1];
	//console.log('Extracted trailing number:', extractedNumber);

	// Step 2: Convert to number and validate
	const clientVariantId = parseInt(extractedNumber, 10);

	if (isNaN(clientVariantId)) {
		//console.log('Extracted number is not valid');
		return null;
	}

	// Step 3: Optional validation against productList if provided
	let foundProduct = null;
	if (productList && productList.length > 0) {
		const foundProduct = productList.find(product =>
			product.clientVariantId === clientVariantId ||
			product.clientVariantId === extractedNumber
		);
		if (foundProduct) {
			//console.log('Found matching product in productList:', foundProduct.label);
			return [clientVariantId, foundProduct];
		} else {
			//console.log('clientVariantId not found in productList, but returning extracted number anyway');
			// Return the extracted number even if not found in productList
			// as the requirement is to extract the number regardless
		}
	}

	//console.log('Final clientVariantId:', clientVariantId);
	return [clientVariantId, foundProduct];
};

export const parseData = (selectedPOs = [], stores = [], productList = [], viewMode = false) => {
	const uniq = (arr) => Array.from(new Set(arr.filter(v => v !== undefined && v !== null)));
	const makeScalarOrSet = (list) => {
		if (list.length === 0) return null;
		if (list.length === 1) return list[0];
		return new Set(list);
	};

	// Unique meta fields
	const orderTypeList = uniq(selectedPOs.map(it => getOrderType(it?.type)))
	const orderType = makeScalarOrSet(orderTypeList);

	const processList = uniq(selectedPOs.map(it => it?.toProcessId));
	const process = makeScalarOrSet(processList);

	const shiftList = uniq(selectedPOs.map(it => it?.shiftId));
	const shift = makeScalarOrSet(shiftList);

	const expectedDeliveryDateList = uniq(selectedPOs.map(it => it?.scheduledAt?.split(' ')[0]))
	const expectedDeliveryDate = makeScalarOrSet(expectedDeliveryDateList);

	// Stores (unique)
	const selectedStoreIds = selectedPOs?.map(po => po?.storeId);
	const selectedStores = viewMode ? stores?.filter(s => selectedStoreIds.includes(s?.id)) : stores;

	const formStoreId = selectedPOs[0]?.fromStore;

	// Headers
	const headers = [
		{ name: 'S.No', id: 'S.No' },
		{ name: 'Item Name', id: 'Item Name' },
		{ name: 'SkuCode', id: 'SkuCode' },
		...selectedStores.map(store => ({
			name: `${store?.name} ${store?.shortName ? `(${store?.shortName})` : ''}`,
			storeName: store?.name,
			shortName: store?.shortName,
			id: store?.id,
		})),
		{ name: 'Total', id: 'Total' },
		{ name: 'Actions', id: 'Actions' },
	];

	// Item-wise aggregation (unchanged)
	const itemAgg = new Map();
	const getClientVariantId = (variantId, fallbackClientVariantId) => {
		if (variantId != null) {
			const found = productList?.flatMap(p => p?.variants || [])?.find(v => v?.id == variantId);
			if (found?.clientVariantId != null) return found.clientVariantId;
		}
		return fallbackClientVariantId ?? null;
	};

	selectedPOs?.forEach(po => {
		const storeId = po?.storeId;
		if (!storeId || !Array.isArray(po?.items)) return;

		po.items.forEach(it => {
			const variantId = it?.variantId ?? it?.itemId ?? null;
			const name = [it?.productName, it?.variantName].filter(Boolean).join(' (') + (it?.variantName ? ')' : '');
			const clientVariantId = getClientVariantId(variantId, it?.clientVariantId);
			const unit = it?.unit || '';

			const key = variantId ?? clientVariantId ?? `${name}::${unit}`;
			if (!itemAgg.has(key)) {
				itemAgg.set(key, {
					variantId,
					productId: it?.productId ?? null,
					name,
					unit,
					clientVariantId,
					perStoreQty: {},
				});
			}
			const entry = itemAgg.get(key);
			const qty = Number(it?.qty ?? 0);
			if (!Number.isNaN(qty) && qty > 0) {
				entry.perStoreQty[storeId] = (entry.perStoreQty[storeId] ?? 0) + qty;
			}
		});
	});

	const rows = [];
	let serial = 1;
	itemAgg.forEach(entry => {
		const totalQty = selectedStores.reduce((sum, s) => sum + (entry.perStoreQty[s.id] ?? 0), 0);
		const row = {
			'S.No': serial++,
			'Item Name': entry.name || '-',
			'SkuCode': entry.clientVariantId ?? '',
			itemId: entry.variantId ?? null,
			Total: totalQty || '',
			...selectedStores.reduce((acc, store) => {
				acc[`${store.id}`] = {
					qty: entry.perStoreQty[store.id] ?? 0,
					unit: entry.unit,
					storeId: store.id,
					itemId: entry.variantId ?? null,
					productId: entry.productId ?? null,
				};
				return acc;
			}, {}),
			Actions: { rowIndex: rows.length },
		};
		rows.push(row);
	});

	const fieldsData = {
		expectedDeliveryDate: new Date(expectedDeliveryDate),
		orderType: orderType === 'ORDER' ? 'PO' : orderType ?? 'CMS',
		internalProcess: process,
		internalShift: shift,
		fromStore: formStoreId,
		date: moment(new Date()).format('DD-MM-YYYY'),
	}
	return {
		headers,
		rows,
		fieldsData,
	};
};

